import { CognitoService } from '@/types/services/cognito';
import 'cross-fetch/polyfill';
import * as AmazonCognitoIdentity from 'amazon-cognito-identity-js';
import { jwtDecode } from 'jwt-decode';
import userUtils from '@/utils/user';
import {
  isCognitoSdkSessionInvalidError,
  triggerForcedSessionLogout,
} from '@/utils/forcedSessionLogout';
import { CognitoSignUpValidation } from '@/types/user';

export const cognitoService: CognitoService = {
  getUserAttributes: async (user: AmazonCognitoIdentity.CognitoUser) => {
    return new Promise(resolve => {
      user.getUserAttributes((error, attributes) => {
        resolve(attributes ?? []);
      });
    });
  },
  refreshUserSession: async () => {
    const cognitoUserPoolData = userUtils.getCognitoUserPoolData();

    const poolData = {
      UserPoolId: cognitoUserPoolData.awsUserPoolId,
      ClientId: cognitoUserPoolData.awsCognitoClientId,
    };

    const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      throw new Error('No current user found');
    }

    return new Promise<string>((resolve, reject) => {
      cognitoUser.getSession(
        (err: Error | null, session: AmazonCognitoIdentity.CognitoUserSession | null) => {
          if (err) {
            console.error('Error refreshing session:', err);
            if (isCognitoSdkSessionInvalidError(err)) {
              triggerForcedSessionLogout();
            }
            reject(err);
            return;
          }

          if (!session) {
            console.error('Session is null');
            reject(new Error('Session is null'));
            return;
          }

          const refreshTokenStr =
            localStorage.getItem('refresh_token') ?? session.getRefreshToken().getToken();
          const refreshTokenObj = new AmazonCognitoIdentity.CognitoRefreshToken({
            RefreshToken: refreshTokenStr,
          });

          cognitoUser.refreshSession(refreshTokenObj, (refreshErr, newSession) => {
            if (refreshErr) {
              console.error('Error refreshing session:', refreshErr);
              if (isCognitoSdkSessionInvalidError(refreshErr)) {
                triggerForcedSessionLogout();
              }
              reject(refreshErr);
              return;
            }

            if (!newSession) {
              console.error('New session is null');
              reject(new Error('New session is null'));
              return;
            }

            const token = newSession.getAccessToken().getJwtToken();

            resolve(token);
          });
        }
      );
    });
  },
  checkCognitoSession: async (isClassCodeStudent: boolean | undefined) => {
    const cognitoUserPoolData = userUtils.getCognitoUserPoolData(isClassCodeStudent);

    //DEMO HOTFIX:
    const poolData = {
      UserPoolId: cognitoUserPoolData.awsUserPoolId,
      ClientId: cognitoUserPoolData.awsCognitoClientId,
    };

    const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);
    const cognitoUser = userPool.getCurrentUser();

    return new Promise((resolve, reject) => {
      cognitoUser?.getSession(async (err: any, session: any) => {
        if (err) {
          if (isCognitoSdkSessionInvalidError(err)) {
            triggerForcedSessionLogout();
          }
          return reject(false);
        }

        if (session.isValid()) {
          const accessToken = session.getAccessToken().getJwtToken();
          const decoded = jwtDecode<any>(accessToken);
          const isGoogleUser = decoded.username?.startsWith('google_');

          if (isGoogleUser) {
            resolve({
              teacherLevel: 1,
              nativeLanguage: 'pt',
            });
          } else {
            const attributes = await cognitoService.getUserAttributes(cognitoUser);

            const nativeLanguage = cognitoService.getNativeLanguageAttribute(attributes);

            const teacherLevel = cognitoService.getTeacherLevelAttribute(attributes);

            resolve({
              teacherLevel: Number(teacherLevel?.Value ?? 1),
              nativeLanguage: nativeLanguage?.Value ?? 'pt',
            });
          }
        } else {
          return reject(false);
        }
      });
    });
  },
  setTeacherLevel: async (level: number) => {
    const cognitoUserPoolData = userUtils.getCognitoUserPoolData();

    const poolData = {
      UserPoolId: cognitoUserPoolData.awsUserPoolId,
      ClientId: cognitoUserPoolData.awsCognitoClientId,
    };

    const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);
    const cognitoUser = userPool.getCurrentUser();

    return new Promise((resolve, reject) => {
      cognitoUser?.getSession((err: any, session: any) => {
        if (err) {
          if (isCognitoSdkSessionInvalidError(err)) {
            triggerForcedSessionLogout();
          }
          return reject(false);
        }

        if (session.isValid()) {
          cognitoUser?.updateAttributes(
            [{ Name: 'custom:teacherLevel', Value: level.toString() }],
            err => {
              if (err) {
                return reject(false);
              }

              resolve(true);
            }
          );
        } else {
          return reject(false);
        }
      });
    });
  },
  getNativeLanguageAttribute: (attributes: AmazonCognitoIdentity.CognitoUserAttribute[]) => {
    return attributes.find(attr => attr.Name === 'custom:nativeLanguage');
  },
  getRoleAttribute: (attributes: AmazonCognitoIdentity.CognitoUserAttribute[]) => {
    return attributes.find(attr => attr.Name === 'custom:role');
  },
  getTeacherLevelAttribute: (attributes: AmazonCognitoIdentity.CognitoUserAttribute[]) => {
    return attributes.find(attr => attr.Name === 'custom:teacherLevel');
  },
  authenticate: async (username: string, password: string, classCode: boolean | undefined) => {
    const authenticationData = {
      Username: username,
      Password: password,
    };

    const authenticationDetails = new AmazonCognitoIdentity.AuthenticationDetails(
      authenticationData
    );

    const cognitoUserPoolData = userUtils.getCognitoUserPoolData(classCode);

    const poolData = {
      UserPoolId: cognitoUserPoolData.awsUserPoolId,
      ClientId: cognitoUserPoolData.awsCognitoClientId,
    };

    const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);

    const userData = {
      Username: username,
      Pool: userPool,
    };

    const cognitoUser = new AmazonCognitoIdentity.CognitoUser(userData);

    return new Promise((resolve, reject) => {
      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: async session => {
          const token = session.getAccessToken().getJwtToken();
          const refreshToken = session.getRefreshToken().getToken();
          localStorage.setItem('refresh_token', refreshToken);
          const userId =
            session.getAccessToken().payload['user_id'] || session.getAccessToken().payload['sub'];
          const attributes = await cognitoService.getUserAttributes(cognitoUser);

          const nativeLanguage = cognitoService.getNativeLanguageAttribute(attributes);
          const role = cognitoService.getRoleAttribute(attributes);

          resolve({
            token,
            userId,
            nativeLanguage: nativeLanguage,
            role: role,
            refreshToken: refreshToken,
          });
        },
        newPasswordRequired: function (userAttributes, requiredAttributes) {
          resolve({
            newPasswordRequired: true,
            requiredAttributes,
            cognitoUser,
          });
        },
        onFailure: function (err: { message: any; code: any }) {
          reject(err.code);
        },
      });
    });
  },

  createAccount: async (
    given_name,
    family_name,
    email,
    password,
    nativeLanguageCode,
    role,
    validationData: CognitoSignUpValidation
  ) => {
    const cognitoUserPoolData = userUtils.getCognitoUserPoolData();

    const poolData = {
      UserPoolId: cognitoUserPoolData.awsUserPoolId,
      ClientId: cognitoUserPoolData.awsCognitoClientId,
    };

    const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);

    const attributeEmail = new AmazonCognitoIdentity.CognitoUserAttribute({
      Name: 'email',
      Value: email,
    });

    const attributeName = new AmazonCognitoIdentity.CognitoUserAttribute({
      Name: 'name',
      Value: given_name,
    });

    const attributeGivenName = new AmazonCognitoIdentity.CognitoUserAttribute({
      Name: 'given_name',
      Value: given_name,
    });

    const attributeFamilyName = new AmazonCognitoIdentity.CognitoUserAttribute({
      Name: 'family_name',
      Value: family_name,
    });

    const attributeNativeLanguage = new AmazonCognitoIdentity.CognitoUserAttribute({
      Name: 'custom:nativeLanguage',
      Value: nativeLanguageCode,
    });

    const attributeRole = new AmazonCognitoIdentity.CognitoUserAttribute({
      Name: 'custom:role',
      Value: role,
    });

    const attributeCreatedAt = new AmazonCognitoIdentity.CognitoUserAttribute({
      Name: 'custom:createdAt',
      Value: new Date().toISOString(),
    });

    const attributeRegistrationQuery = new AmazonCognitoIdentity.CognitoUserAttribute({
      Name: 'custom:registrationQuery',
      Value: localStorage.getItem('registration-query') || '',
    });

    return new Promise((resolve, reject) => {
      userPool.signUp(
        email,
        password,
        [
          attributeEmail,
          attributeName,
          attributeGivenName,
          attributeFamilyName,
          attributeNativeLanguage,
          attributeRole,
          attributeCreatedAt,
          attributeRegistrationQuery,
        ],
        Object.keys(validationData).map(
          key =>
            new AmazonCognitoIdentity.CognitoUserAttribute({
              Name: key,
              Value: (validationData as any)[key],
            })
        ),
        (err, result) => {
          if (err) {
            return reject(err);
          }

          return resolve(result?.userSub);
        }
      );
    });
  },

  createClassCodeAccount: async (
    email,
    username,
    password,
    nativeLanguageCode,
    role,
    validationData: CognitoSignUpValidation
  ) => {
    const cognitoUserPoolData = userUtils.getCognitoUserPoolData(true);

    const poolData = {
      UserPoolId: cognitoUserPoolData.awsUserPoolId,
      ClientId: cognitoUserPoolData.awsCognitoClientId,
    };

    const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);

    const attributeEmail = new AmazonCognitoIdentity.CognitoUserAttribute({
      Name: 'email',
      Value: email,
    });

    const attributeNativeLanguage = new AmazonCognitoIdentity.CognitoUserAttribute({
      Name: 'custom:nativeLanguage',
      Value: nativeLanguageCode,
    });

    const attributeRole = new AmazonCognitoIdentity.CognitoUserAttribute({
      Name: 'custom:role',
      Value: role,
    });

    const attributeCreatedAt = new AmazonCognitoIdentity.CognitoUserAttribute({
      Name: 'custom:createdAt',
      Value: new Date().toISOString(),
    });

    return new Promise((resolve, reject) => {
      userPool.signUp(
        username,
        password,
        [attributeEmail, attributeNativeLanguage, attributeRole, attributeCreatedAt],
        Object.keys(validationData).map(
          key =>
            new AmazonCognitoIdentity.CognitoUserAttribute({
              Name: key,
              Value: (validationData as any)[key],
            })
        ),
        (err, result) => {
          if (err) {
            return reject(err);
          }

          return resolve(result?.userSub);
        }
      );
    });
  },

  verifyAccount: function (email, verificationCode) {
    const cognitoUserPoolData = userUtils.getCognitoUserPoolData();

    const poolData = {
      UserPoolId: cognitoUserPoolData.awsUserPoolId,
      ClientId: cognitoUserPoolData.awsCognitoClientId,
    };

    const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);

    const userData = {
      Username: email,
      Pool: userPool,
    };

    const cognitoUser = new AmazonCognitoIdentity.CognitoUser(userData);

    return new Promise((resolve, reject) => {
      cognitoUser.confirmRegistration(verificationCode, true, function (err, result) {
        if (err) {
          return reject(err);
        }

        return resolve(result);
      });
    });
  },
  confirmForgotPassword: function (email, verificationCode, newPassword) {
    const cognitoUserPoolData = userUtils.getCognitoUserPoolData();

    const poolData = {
      UserPoolId: cognitoUserPoolData.awsUserPoolId,
      ClientId: cognitoUserPoolData.awsCognitoClientId,
    };

    const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);

    const userData = {
      Username: email,
      Pool: userPool,
    };

    const cognitoUser = new AmazonCognitoIdentity.CognitoUser(userData);

    return new Promise((resolve, reject) => {
      cognitoUser.confirmPassword(verificationCode, newPassword, {
        onSuccess: function () {
          resolve(true);
        },
        onFailure: function (err) {
          reject(err);
        },
      });
    });
  },
  forgotPassword: function (email) {
    const cognitoUserPoolData = userUtils.getCognitoUserPoolData();

    const poolData = {
      UserPoolId: cognitoUserPoolData.awsUserPoolId,
      ClientId: cognitoUserPoolData.awsCognitoClientId,
    };

    const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);

    const userData = {
      Username: email,
      Pool: userPool,
    };

    const cognitoUser = new AmazonCognitoIdentity.CognitoUser(userData);

    return new Promise((resolve, reject) => {
      cognitoUser.forgotPassword({
        onSuccess: function () {
          resolve(true);
        },
        onFailure: function (err) {
          reject(err);
        },
      });
    });
  },
  resendVerificationCode: function (email) {
    const cognitoUserPoolData = userUtils.getCognitoUserPoolData();

    const poolData = {
      UserPoolId: cognitoUserPoolData.awsUserPoolId,
      ClientId: cognitoUserPoolData.awsCognitoClientId,
    };

    const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);

    const userData = {
      Username: email,
      Pool: userPool,
    };

    const cognitoUser = new AmazonCognitoIdentity.CognitoUser(userData);

    return new Promise((resolve, reject) => {
      cognitoUser.resendConfirmationCode(function (err: any, result) {
        if (err) {
          return reject(err.code);
        }

        return resolve(result);
      });
    });
  },
  forceChangeNewPassword: async (cognitoUser, requiredAttributes, password) => {
    return new Promise((resolve, reject) => {
      cognitoUser.completeNewPasswordChallenge(password, requiredAttributes, {
        onSuccess: async session => {
          const token = session.getAccessToken().getJwtToken();
          const userId =
            session.getAccessToken().payload['user_id'] || session.getAccessToken().payload['sub'];
          const refreshToken = session.getRefreshToken().getToken();
          localStorage.setItem('refresh_token', refreshToken);
          const attributes = await cognitoService.getUserAttributes(cognitoUser);

          const nativeLanguage = cognitoService.getNativeLanguageAttribute(attributes);
          const role = cognitoService.getRoleAttribute(attributes);

          resolve({
            token,
            userId,
            nativeLanguage: nativeLanguage,
            role: role,
            refreshToken: refreshToken,
          });
        },
        onFailure: err => {
          reject(err);
        },
      });
    });
  },
  updatePassword: async (username, password, newPassword, isClassCodeStudent) => {
    const cognitoUserPoolData = userUtils.getCognitoUserPoolData(isClassCodeStudent);

    const poolData = {
      UserPoolId: cognitoUserPoolData.awsUserPoolId,
      ClientId: cognitoUserPoolData.awsCognitoClientId,
    };

    const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);
    const cognitoUser = userPool.getCurrentUser();

    if (cognitoUser) {
      const authenticationData = {
        Username: username,
        Password: password,
      };

      const authenticationDetails = new AmazonCognitoIdentity.AuthenticationDetails(
        authenticationData
      );

      return new Promise((resolve, reject) => {
        cognitoUser.authenticateUser(authenticationDetails, {
          onSuccess: function () {
            cognitoUser.changePassword(password, newPassword, (error: any) => {
              if (!error) {
                resolve(true);
              } else if (error.message) {
                reject(error.code);
              }
            });
          },
          onFailure: function (error: any) {
            reject(error.code);
          },
        });
      });
    }
  },
};
